import type { NavItem, SNavItemsLoadedEvent } from "@avalara/skylab-sdk";
import { MouseEvent, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import isOneWebDomainRuntime from "../../shared/avaAppRuntime";
import {
    AVA_APP_NAME,
    avataxAPUrls,
    EXEMPTIONS_SUPERNAV_DATA_REF,
    SETTINGS_SUPERNAV_DATA_REF,
} from "../../shared/constants";
import { resolveActiveNavRef } from "../navigation/activeNavResolver";

type NavElement = HTMLElement & {
    navHandler?: (e: MouseEvent, url: string) => void;
    active?: string;
    navItems?: string | NavItem[];
};

const NAV_ITEMS_LOADED_EVENT = "s-navitemsloaded";

// Always re-queried (never captured once): in standalone the nav renders only after
// session.hasToken, and a company change can remount it, so the element may not exist yet or may
// be a different instance than the one found on an earlier run.
const getNavElements = (): NavElement[] =>
    [document.querySelector("aui-sidenav"), document.querySelector("aui-supernav")].filter(
        (el): el is NavElement => el !== null
    );

// Reads the nav item tree already present on an element (e.g. AvaApp, where s-navitemsloaded has
// usually fired before ECM mounts). `navItems` may be a JSON string or an array.
const readNavItems = (): NavItem[] | undefined => {
    for (const el of getNavElements()) {
        const raw = el.navItems;
        if (!raw) continue;
        try {
            const items = typeof raw === "string" ? (JSON.parse(raw) as NavItem[]) : raw;
            if (items.length > 0) return items;
        } catch {
            // Malformed navItems string: try the next element.
        }
    }
    return undefined;
};

/**
 * Keeps `<aui-sidenav>`/`<aui-supernav>` in sync with the current route: registers our own
 * `navHandler` so sidenav/supernav clicks use React Router instead of a full-page reload, and
 * resolves + applies the correct `active` dataRef against the full nav item tree (including
 * submenus). Skylab's nav components only ever expose a single static `active` dataRef, so this
 * has to be resolved ourselves and written as a DOM property rather than a React prop.
 *
 * Runs in both runtimes, just against a different element:
 * - In AvaApp (OWD), ECM mounts only its content inside <main> and does not render its own
 *   <AuiSidenav>/<AuiSupernav> — AvaApp's shell renders those outside this app's DOM, so we reach
 *   them via querySelector.
 * - In standalone, this app renders its own instance (via `navMemo` in `Application.tsx`), which
 *   receives the same nav item tree from the Web Platform Navigation Service, so the same
 *   resolution applies there too; only the route prefix differs, since OWD mounts this app under
 *   the `/exemptions` basename while standalone does not.
 *
 * Extracted from `Application.tsx` to keep that component focused on session-level concerns.
 */
export const useActiveNavSync = (
    handleNavigation: (e: MouseEvent, url: string) => void,
    shouldSelectSettingsInSupernav: boolean
): void => {
    const location = useLocation();
    // Populated from the nav's "s-navitemsloaded" event (or read off the element) so the active
    // nav item can be re-resolved against the current route.
    const navItemsRef = useRef<NavItem[]>([]);

    useEffect(() => {
        const currentFullPath = isOneWebDomainRuntime()
            ? `/${AVA_APP_NAME}${location.pathname}`
            : location.pathname;

        const applyNavHandler = (navElements: NavElement[]) => {
            navElements.forEach(el => {
                el.navHandler = handleNavigation;
            });
        };

        const applyActiveNav = (navElements: NavElement[]) => {
            // AvaTax AP subscribers have no real "Exemptions" workflow — vendor management is
            // the closest thing they have to a settings/admin area — so for them specifically,
            // vendor routes should highlight the Settings tab instead of whatever the nav tree
            // would otherwise resolve. The Settings top-level item has no `url` of its own (its
            // urls live on its `menuItems` children), so the override is matched by its stable
            // dataRef rather than by url.
            const isAvataxAPVendorRoute: boolean = avataxAPUrls.some((url: string) =>
                currentFullPath.includes(url)
            );
            const settingsOverrideRef =
                shouldSelectSettingsInSupernav && isAvataxAPVendorRoute
                    ? navItemsRef.current.find(item => item.dataRef === SETTINGS_SUPERNAV_DATA_REF)
                          ?.dataRef
                    : undefined;

            // Falls back to this app's own top-level tab whenever nothing more specific matches
            // (e.g. the bare app root, whose top-level nav item has no `url` of its own — see
            // activeNavResolver.test.ts). avaapp-config's `activeNavTab` only seeds this same ref
            // once, before this app's JS runs, and is never re-applied on later navigations, so
            // without this fallback a stale submenu highlight from an earlier route would stick.
            const activeRef =
                settingsOverrideRef ??
                resolveActiveNavRef(navItemsRef.current, currentFullPath) ??
                EXEMPTIONS_SUPERNAV_DATA_REF;
            navElements.forEach(el => {
                el.active = activeRef;
            });
        };

        const navElements = getNavElements();

        const sync = () => {
            const elements = getNavElements();
            applyNavHandler(elements);
            applyActiveNav(elements);
        };

        // Captures the nav item tree (with each item's `url` and `dataRef`) broadcast by the Web
        // Platform Navigation Service, so the route -> dataRef lookup has data to match against,
        // then re-applies nav state now that it's available.
        const handleNavItemsLoaded = (event: Event) => {
            const navItems = (event as CustomEvent<SNavItemsLoadedEvent>).detail?.navItems;
            if (navItems) navItemsRef.current = navItems;
            sync();
        };

        // Seed from whatever is already on the element so we don't depend on the event having
        // fired after this effect mounted.
        const existingNavItems = readNavItems();
        if (existingNavItems) navItemsRef.current = existingNavItems;
        sync();

        // Listens on each element directly rather than document: the element may not exist yet
        // when this effect runs (standalone renders nav only after session.hasToken), but once it
        // does, that's the instance that actually dispatches s-navitemsloaded.
        navElements.forEach(el =>
            el.addEventListener(NAV_ITEMS_LOADED_EVENT, handleNavItemsLoaded)
        );

        return () => {
            navElements.forEach(el =>
                el.removeEventListener(NAV_ITEMS_LOADED_EVENT, handleNavItemsLoaded)
            );
        };
    }, [location.pathname, shouldSelectSettingsInSupernav, handleNavigation]);
};

export default useActiveNavSync;
